/**
 * Content Ideas (trending short-form-video ideas + storyboards) API (lane B6).
 *
 * Backend: `backend/servers/social/routers/content_ideas.py`.
 *   GET  /api/content-ideas?vertical=&channel=&format=&difficulty=&trend_status=&idea_type=&humor_format=
 *        &hook_type=&pillar=&min_score=&sort=trend|score&q=&client_brand_id=&limit=&offset=
 *   GET  /api/content-ideas/meta?client_brand_id=        facet counts for the filter pills
 *   GET  /api/content-ideas/{id}?client_brand_id=        full idea with storyboard beats
 *   POST /api/content-ideas/{id}/to-calendar             DRAFT brief post (never schedules)
 *   POST /api/content-ideas/{id}/use                     counts a composer ("Write it now") pick
 *
 * Every call passes the switcher's selected client brand: the catalog is
 * scoped to that client's verticals, and a brand-specific idea is only visible
 * (and pickable) with its own brand.
 *
 * Types are hand-written (the generated `types.gen.ts` is regenerated by the
 * manager at integration).
 *
 * @module api/contentIdeas
 */

import { useApiQuery } from "@plerity/dashboard-shared/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isUuid } from "../lib/uuid";
import { client } from "./client";
import { socialKeys } from "./keys";
import { objectResponseGuard } from "./schemas";

// ── Types ──────────────────────────────────────────────────────────────────────

export type IdeaFacet =
  | "vertical"
  | "channel"
  | "format"
  | "difficulty"
  | "trend_status"
  | "idea_type"
  | "humor_format"
  | "hook_type"
  | "pillar";

/** Library ordering: `trend` (default — rising first, then most used) or `score` (ICE score, high to low). */
export type IdeaSort = "trend" | "score";

/** Real, already-held media an idea is built from (migration 098, 2026-10-07) —
 * `kind` is loosely typed server-side (`list[dict]`); unknown values render as-is. */
export interface IdeaSourceAsset {
  kind: "dam" | "content_library" | "drive" | "footage" | string;
  id: string | number;
  note?: string | null;
}

export interface IdeaSummary {
  id: string;
  idea_slug: string;
  title: string;
  vertical: string;
  channel: string;
  platforms: string[];
  format: string;
  hook: string;
  idea_type: string;
  difficulty: string;
  trend_status: string;
  usage_count: number;
  compliance_notes: string;
  refreshed_at: string;
  client_brand_id: string | null;
  /** Real, already-held media this idea is built from. */
  source_assets: IdeaSourceAsset[];
  /** GENERATED column derived from `source_assets` — never set directly. */
  ready_from_library: boolean;
  /** v2 (2026-10-11): ICE score 0-100 (impact × confidence × ease / 10). */
  ice_score?: number | null;
  humor_format?: string | null;
  hook_type?: string | null;
  pillar?: string | null;
  originality_class?: string | null;
  /** Estimated finished-reel length in seconds. */
  est_duration_s?: number | null;
  evidence_count?: number;
  shot_count?: number;
  /** Migration 115: number of attached reference images/clips. */
  reference_count?: number;
}

/** Inspiration media shown beside an idea (migration 115) — the trending
 * still/clip to replicate. Reference only: never production media, never posted. */
export interface IdeaReferenceMedia {
  kind: "image" | "video";
  url: string;
  caption?: string | null;
  credit?: string | null;
  source_url?: string | null;
  alt?: string | null;
  reference_only?: boolean;
}

/** Performance numbers behind one evidence item (all optional). */
export interface EvidenceMetric {
  views?: number | null;
  /** Multiple of the brand's median for that media family (3.2 = 3.2× typical). */
  performance_index?: number | null;
  /** Shares ÷ reach, as a fraction (0.041 = 4.1%). */
  share_rate?: number | null;
}

/** One piece of evidence an idea is grounded in (an archive post or a research reference). */
export interface IdeaEvidence {
  kind: string;
  ref?: string | number | null;
  label: string;
  url?: string | null;
  metric?: EvidenceMetric | null;
  note?: string | null;
}

/** One renderer-ready shot. `text_*_s` are reel-timeline seconds. */
export interface IdeaShot {
  shot_no: number;
  roll: string;
  source_kind: string;
  source_asset_id?: string | number | null;
  in_s?: number | null;
  out_s?: number | null;
  duration_s: number;
  visual: string;
  on_screen_text?: string | null;
  text_start_s?: number | null;
  text_end_s?: number | null;
  audio_cue?: string | null;
  beat?: string | null;
  purpose?: string | null;
}

/** One storyboard beat — external research content, every field optional. */
export interface StoryboardBeat {
  beat?: string | number | null;
  time?: string | null;
  shot?: string | null;
  text_overlay?: string | null;
  audio?: string | null;
  [key: string]: unknown;
}

export interface IdeaDetail extends IdeaSummary {
  org_id: number;
  is_global: boolean;
  storyboard: StoryboardBeat[];
  why_it_works: string;
  example_accounts: string[];
  source_urls: string[];
  production_notes: string;
  cta_pattern: string;
  research_source: string;
  status: string;
  created_at: string;
  updated_at: string;
  /** v2: empty on legacy ideas — the UI falls back to `storyboard`. */
  shot_list?: IdeaShot[];
  evidence?: IdeaEvidence[];
  ice_impact?: number | null;
  ice_confidence?: number | null;
  ice_ease?: number | null;
  score_rationale?: string | null;
  audio_lane?: string | null;
  format_archetype?: string | null;
  /** "code: message" strings from the storyboard linter. */
  storyboard_warnings?: string[];
  /** Migration 115: trending still/clip this idea replicates (reference only). */
  reference_media?: IdeaReferenceMedia[];
}

export interface IdeaListData {
  ideas: IdeaSummary[];
  total: number;
}

export interface IdeaMetaData {
  facets: Partial<Record<IdeaFacet, Record<string, number>>>;
}

export interface IdeaFilters {
  vertical?: string | null;
  channel?: string | null;
  format?: string | null;
  difficulty?: string | null;
  trend_status?: string | null;
  idea_type?: string | null;
  humor_format?: string | null;
  hook_type?: string | null;
  pillar?: string | null;
  /** Minimum ICE score (0-100). */
  minScore?: number | null;
  sort?: IdeaSort | null;
  q?: string | null;
  limit?: number;
  offset?: number;
  /** true = already buildable from held media only; false = needs a shoot; omit = either. */
  readyFromLibrary?: boolean | null;
}

export interface ToCalendarBody {
  client_brand_id?: string | null;
  /** YYYY-MM-DD — informational planned shoot/publish date; never schedules. */
  planned_date?: string | null;
  notes?: string | null;
}

export interface ToCalendarResult {
  post_id: string;
  status: "draft";
  idea_slug: string;
}

export interface IdeaUseResult {
  idea_id: string;
  usage_count: number;
}

// ── Params ─────────────────────────────────────────────────────────────────────

/** Default page size for the library grid. */
export const IDEAS_PAGE_SIZE = 24;

const brandParam = (brandId: string | null | undefined): Record<string, string> =>
  brandId && isUuid(brandId) ? { client_brand_id: brandId } : {};

/** Query params for the list call — empty filters are dropped, `q` is trimmed. */
export function ideaListParams(brandId: string | null, filters: IdeaFilters = {}): Record<string, string | number> {
  const params: Record<string, string | number> = { ...brandParam(brandId) };
  for (const key of [
    "vertical",
    "channel",
    "format",
    "difficulty",
    "trend_status",
    "idea_type",
    "humor_format",
    "hook_type",
    "pillar",
  ] as const) {
    const value = filters[key];
    if (value) params[key] = value;
  }
  const q = filters.q?.trim();
  if (q) params.q = q;
  if (typeof filters.minScore === "number" && filters.minScore > 0) params.min_score = filters.minScore;
  if (filters.sort === "score") params.sort = "score";
  if (typeof filters.readyFromLibrary === "boolean") params.ready_from_library = String(filters.readyFromLibrary);
  params.limit = filters.limit ?? IDEAS_PAGE_SIZE;
  if (filters.offset) params.offset = filters.offset;
  return params;
}

// ── Queries ────────────────────────────────────────────────────────────────────

export function useContentIdeas(
  brandId: string | null,
  filters: IdeaFilters = {},
  options: { enabled?: boolean; keepPrevious?: boolean } = {}
) {
  const params = ideaListParams(brandId, filters);
  return useApiQuery<IdeaListData>(socialKeys.list("content-ideas", params), "/api/content-ideas", {
    axiosConfig: { params },
    schema: objectResponseGuard<IdeaListData>(),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    ...(options.keepPrevious ? { placeholderData: (prev: IdeaListData | undefined) => prev } : {}),
  });
}

export function useContentIdeaMeta(brandId: string | null) {
  const params = brandParam(brandId);
  return useApiQuery<IdeaMetaData>(socialKeys.list("content-ideas-meta", params), "/api/content-ideas/meta", {
    axiosConfig: { params },
    schema: objectResponseGuard<IdeaMetaData>(),
    staleTime: 5 * 60_000,
  });
}

export function useContentIdea(ideaId: string | null, brandId: string | null) {
  const params = brandParam(brandId);
  return useApiQuery<{ idea: IdeaDetail }>(
    socialKeys.detail("content-ideas", `${ideaId ?? ""}:${params.client_brand_id ?? ""}`),
    `/api/content-ideas/${ideaId ?? ""}`,
    {
      enabled: isUuid(ideaId),
      axiosConfig: { params },
      schema: objectResponseGuard<{ idea: IdeaDetail }>(),
    }
  );
}

// ── Mutations ──────────────────────────────────────────────────────────────────

function useInvalidateIdeas() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: socialKeys.resource("content-ideas") });
  };
}

/** "Add to calendar" — creates one DRAFT brief post (never a schedule). */
export function useIdeaToCalendar() {
  const qc = useQueryClient();
  const invalidateIdeas = useInvalidateIdeas();
  return useMutation({
    mutationFn: async ({ ideaId, body }: { ideaId: string; body: ToCalendarBody }) => {
      const res = await client.post<{ data: ToCalendarResult }>(`/api/content-ideas/${ideaId}/to-calendar`, body);
      return res.data.data;
    },
    onSuccess: () => {
      invalidateIdeas();
      void qc.invalidateQueries({ queryKey: socialKeys.list("posts") });
    },
  });
}

/** Counts a composer pick ("Write it now"). Fire-and-forget from the UI. */
export function useRecordIdeaUse() {
  const invalidateIdeas = useInvalidateIdeas();
  return useMutation({
    mutationFn: async ({ ideaId, brandId }: { ideaId: string; brandId: string | null }) => {
      const res = await client.post<{ data: IdeaUseResult }>(`/api/content-ideas/${ideaId}/use`, {
        client_brand_id: brandId && isUuid(brandId) ? brandId : null,
      });
      return res.data.data;
    },
    onSuccess: invalidateIdeas,
  });
}
